import { API_BASE_URL } from '../constants';
import { getCurrentUser } from './authService';

export interface CartItemResponse {
  id: number;
  sessionId: string;
  customerMobile: string;
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  serviceId: number;
  serviceName: string;
  serviceType: string;
  actualPrice: number;
  serviceCost: number;
  quantity: number;
  warrantyTypeId: number;
  warrantyName: string;
  warrantyPeriod: number;
  createdAt: string;
  updatedAt: string;
}

export interface CartResponse {
  entity: CartItemResponse[];
  isSuccess: boolean;
  errors: string | null;
  exception: string | null;
  message: string;
}

export interface AddToCartRequest {
  sessionId: string;
  customerMobile: string;
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  serviceId: number;
  serviceName: string;
  serviceType: string;
  actualPrice: number;
  serviceCost: number;
  quantity: number;
  warrantyTypeId: number;
  warrantyName: string;
  warrantyPeriod: number;
}

export interface RemoveFromCartRequest {
  cartItemId: number;
}

// Generate a session ID (you might want to store this in localStorage)
export const getSessionId = (): string => {
  let sessionId = localStorage.getItem('minto_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('minto_session_id', sessionId);
  }
  return sessionId;
};

export const getCart = async (): Promise<CartItemResponse[]> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not logged in');
    }

    const sessionId = getSessionId();
    const response = await fetch(
      `${API_BASE_URL}/cart?sessionId=${sessionId}&customerMobile=${user.phoneNumber}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    const data: CartResponse = await response.json();
    
    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to fetch cart');
    }

    return data.entity || [];
  } catch (error) {
    console.error('Error fetching cart:', error);
    throw error;
  }
};

export const addToCart = async (request: AddToCartRequest): Promise<number> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cart/additem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to add item to cart');
    }

    const data = await response.json();
    
    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to add item to cart');
    }

    // Return the cart item ID from the response
    return data.entity?.id || data.entity || 0;
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

export const removeFromCart = async (request: RemoveFromCartRequest): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cart/remove`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to remove item from cart');
    }

    const data = await response.json();
    
    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to remove item from cart');
    }
  } catch (error) {
    console.error('Error removing from cart:', error);
    throw error;
  }
}; 